'use client'

import {
  PropsWithChildren,
  createContext,
  useContext,
  useOptimistic,
} from 'react'

const createOptimisticStateProvider = <S, A>(
  reducer: (state: S, action: A) => S,
) => {
  const StateContext = createContext<{
    state: S
    dispatch: (action: A) => void
  } | null>(null)

  type StateProviderProps = PropsWithChildren<{
    initialState: S
  }>

  const StateProvider = ({ children, initialState }: StateProviderProps) => {
    const [state, dispatch] = useOptimistic(initialState, reducer)

    return (
      <StateContext.Provider value={{ state, dispatch }}>
        {children}
      </StateContext.Provider>
    )
  }

  const useState = () => {
    const context = useContext(StateContext)
    if (!context) {
      throw new Error(
        'Optimistic state hook must be used within a corresponding provider',
      )
    }
    return context
  }

  return [StateProvider, useState] as const
}

export default createOptimisticStateProvider
